import styled from "styled-components";
import { SmallText } from "./common";

type ActiveProps = {
    active?: boolean;
};

type AnalyticsCardProps = {
    span?: number;
    minHeight?: string;
};

type CountProps = {
    count?: number;
};

type MetricBarsProps = CountProps & {
    $compact?: boolean;
    $stackOnMobile?: boolean;
};

type HorizontalMobileProps = {
    $horizontalOnMobile?: boolean;
};

type TrendTrackProps = HorizontalMobileProps & {
    $compact?: boolean;
};

type TrendBarProps = TrendTrackProps & {
    height: number;
    hasValue?: boolean;
    $ready?: boolean;
    $delay?: string;
};

type TrendBarSegmentProps = {
    flex?: number;
    minHeight?: number;
    background?: string;
};

type DonutProgressProps = {
    $circumference: number;
    $offset: number;
    $ready?: boolean;
    $delay?: string;
};

export const AnalyticsShell = styled.div`
    padding: 1rem;
    display: grid;
    gap: 1rem;
    align-content: start;
`;

export const AnalyticsHero = styled.div`
    border: 1px solid var(--calendar-border);
    background: linear-gradient(135deg, var(--calendar-surface-secondary), var(--calendar-surface));
    border-radius: var(--calendar-radius-shell);
    padding: 1rem;
    display: grid;
    gap: 1rem;
`;

export const AnalyticsHeroGrid = styled.div`
    display: grid;
    grid-template-columns: 1.3fr 0.9fr;
    gap: 1rem;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
`;

export const AnalyticsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
`;

export const AnalyticsCard = styled.div<AnalyticsCardProps>`
    grid-column: span ${p => p.span || 12};
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    border-radius: var(--calendar-radius-card);
    padding: 1rem;
    display: grid;
    gap: 0.8rem;
    min-height: ${p => p.minHeight || "auto"};
    align-content: start;
    min-width: 0;

    > * {
        min-width: 0;
        max-width: 100%;
    }

    @media (max-width: 980px) {
        grid-column: span 1;
    }
`;

export const CardTitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    min-width: 0;

    > div:first-child {
        display: grid;
        gap: 0.2rem;
        width: 100%;
        min-width: 0;
        flex: 1 1 auto;
        align-content: start;
    }

    @media (max-width: 640px) {
        flex-direction: column;
    }
`;

export const MetricValue = styled.div`
    font-size: 1.9rem;
    line-height: 1;
    font-weight: 800;
`;

export const ChipRow = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

export const FilterChip = styled.button<ActiveProps>`
    border: 1px solid ${p => p.active ? "transparent" : "var(--calendar-border)"};
    background: ${p => p.active ? "var(--calendar-accent)" : "var(--calendar-surface)"};
    color: ${p => p.active ? "#fff" : "var(--calendar-text-muted)"};
    min-height: 34px;
    padding: 0.45rem 0.8rem;
    border-radius: var(--calendar-radius-badge);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

export const MiniStatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const MiniStat = styled.div`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface);
    border-radius: var(--calendar-radius-card);
    padding: 0.8rem;
    display: grid;
    gap: 0.25rem;
    align-content: start;
    min-width: 0;

    > * {
        min-width: 0;
        max-width: 100%;
    }
`;

export const TrendBars = styled.div<CountProps>`
    display: grid;
    grid-template-columns: repeat(${p => p.count || 7}, minmax(0, 1fr));
    align-items: end;
    gap: 0.5rem;
    min-height: 172px;
`;

export const MetricBars = styled.div<MetricBarsProps>`
    display: grid;
    grid-template-columns: repeat(${p => p.count || 4}, minmax(${p => p.$compact ? "44px" : "72px"}, 1fr));
    align-items: end;
    gap: ${p => p.$compact ? "0.35rem" : "0.75rem"};
    min-height: 172px;

    @media (max-width: 680px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 760px) {
        grid-template-columns: ${p => p.$stackOnMobile ? "1fr" : "repeat(2, minmax(0, 1fr))"};
        min-height: ${p => p.$stackOnMobile ? "auto" : "172px"};
    }
`;

export const TrendColumn = styled.div<HorizontalMobileProps>`
    display: grid;
    gap: 0.45rem;
    justify-items: center;
    text-align: center;
    min-width: 0;

    @media (max-width: 760px) {
        grid-template-columns: ${p => p.$horizontalOnMobile ? "minmax(88px, auto) minmax(0, 1fr) auto" : "1fr"};
        align-items: center;
        justify-items: ${p => p.$horizontalOnMobile ? "stretch" : "center"};
        column-gap: ${p => p.$horizontalOnMobile ? "0.7rem" : "0"};
    }
`;

export const TrendStack = styled.div<HorizontalMobileProps>`
    width: 100%;
    min-height: 142px;
    display: flex;
    align-items: flex-end;
    justify-content: center;

    @media (max-width: 760px) {
        min-height: ${p => p.$horizontalOnMobile ? "auto" : "142px"};
        align-items: ${p => p.$horizontalOnMobile ? "center" : "flex-end"};
        justify-content: ${p => p.$horizontalOnMobile ? "stretch" : "center"};
    }
`;

export const TrendTrack = styled.div<TrendTrackProps>`
    width: 100%;
    max-width: ${p => p.$compact ? "30px" : "46px"};
    min-height: 142px;
    padding: ${p => p.$compact ? "8px 6px" : "10px 8px"};
    border-radius: var(--calendar-radius-card);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: linear-gradient(180deg, var(--calendar-surface) 0%, rgba(255, 255, 255, 0.02) 100%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);

    @media (max-width: 760px) {
        max-width: ${p => p.$horizontalOnMobile ? "100%" : "46px"};
        min-height: ${p => p.$horizontalOnMobile ? "38px" : "142px"};
        padding: ${p => p.$horizontalOnMobile ? "8px" : "10px 8px"};
        align-items: ${p => p.$horizontalOnMobile ? "center" : "flex-end"};
    }
`;

export const TrendBar = styled.div<TrendBarProps>`
    width: 100%;
    max-width: ${p => p.$compact ? "14px" : "30px"};
    height: ${p => `${p.height}px`};
    min-height: ${p => p.hasValue ? "18px" : "10px"};
    border-radius: var(--calendar-radius-task);
    overflow: hidden;
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-start;
    box-shadow: 0 16px 28px rgba(8, 12, 24, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transform-origin: center bottom;
    transition: height 0.42s cubic-bezier(0.2, 0.9, 0.2, 1), transform 0.56s cubic-bezier(0.2, 0.9, 0.2, 1), opacity 0.32s ease, box-shadow 0.28s ease;
    transition-delay: ${p => p.$delay || "0s"};
    transform: translateY(${p => p.hasValue ? "0" : "4px"}) scaleY(${p => p.$ready ? 1 : 0.12});
    opacity: ${p => p.$ready ? 1 : 0.35};

    @media (max-width: 760px) {
        max-width: ${p => p.$horizontalOnMobile ? "none" : "30px"};
        width: ${p => p.$horizontalOnMobile ? `${p.height}px` : "100%"};
        height: ${p => p.$horizontalOnMobile ? "18px" : `${p.height}px`};
        min-height: ${p => p.$horizontalOnMobile ? "18px" : (p.hasValue ? "18px" : "10px")};
        border-radius: ${p => p.$horizontalOnMobile ? "var(--calendar-radius-badge)" : "var(--calendar-radius-task)"};
        transform: ${p => p.$horizontalOnMobile
        ? `translateX(${p.hasValue ? "0" : "-4px"}) scaleX(${p.$ready ? 1 : 0.12})`
        : `translateY(${p.hasValue ? "0" : "4px"}) scaleY(${p.$ready ? 1 : 0.12})`};
        transform-origin: ${p => p.$horizontalOnMobile ? "left center" : "center bottom"};
    }
`;

export const ChartCaption = styled(SmallText)`
    text-align: center;
    font-size: 11px;
    line-height: 1.25;
`;

export const TrendBarSegment = styled.div<TrendBarSegmentProps>`
    flex: ${p => p.flex || 0};
    min-height: ${p => p.minHeight || 0}px;
    background: ${p => p.background || "var(--calendar-accent)"};
    transition: flex 0.42s cubic-bezier(0.2, 0.9, 0.2, 1), min-height 0.32s ease, opacity 0.24s ease;
    position: relative;

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.28), rgba(255, 255, 255, 0));
        opacity: 0.55;
        pointer-events: none;
    }
`;

export const DonutWrap = styled.div`
    width: min(176px, 100%);
    aspect-ratio: 1;
    border-radius: var(--calendar-radius-round);
    margin: 0 auto;
    position: relative;
    padding: 12px;
    display: grid;
    place-items: center;
    background: linear-gradient(180deg, var(--calendar-surface) 0%, rgba(255, 255, 255, 0.02) 100%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
`;

export const DonutSvg = styled.svg`
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
    overflow: visible;
    filter: drop-shadow(0 16px 28px rgba(8, 12, 24, 0.2));
`;

export const DonutTrack = styled.circle`
    fill: none;
    stroke: rgba(255, 255, 255, 0.08);
    stroke-width: 12;
`;

export const DonutProgress = styled.circle<DonutProgressProps>`
    fill: none;
    stroke: url(#analyticsFocusRingGradient);
    stroke-width: 12;
    stroke-linecap: round;
    stroke-dasharray: ${p => p.$circumference};
    stroke-dashoffset: ${p => p.$ready ? p.$offset : p.$circumference};
    transition: stroke-dashoffset 0.72s cubic-bezier(0.2, 0.9, 0.2, 1), opacity 0.32s ease;
    transition-delay: ${p => p.$delay || "0s"};
    opacity: ${p => p.$ready ? 1 : 0.3};
`;

export const DonutCenter = styled.div`
    position: absolute;
    inset: 34px;
    border-radius: var(--calendar-radius-round);
    border: 1px solid var(--calendar-border);
    background: linear-gradient(180deg, var(--calendar-surface-secondary) 0%, var(--calendar-surface) 100%);
    display: grid;
    place-items: center;
    text-align: center;

    @media (max-width: 680px) {
        inset: 24%;
    }
`;

export const EmptyStateCard = styled.div`
    border: 1px dashed var(--calendar-border-strong);
    background: var(--calendar-surface);
    border-radius: var(--calendar-radius-card);
    padding: 1rem;
    display: grid;
    gap: 0.45rem;
    min-height: 160px;
    place-content: center;
    text-align: center;
`;
